import React, {
	Children,
	cloneElement,
	isValidElement,
	useCallback,
	useEffect,
	useImperativeHandle,
	useRef,
	useState,
} from "react";
import BemIt, { addClass } from "@gtechdoodler/bem-it";
import ListItem from "./ListItem";
import StyledList from "./List.styles";
import { ListProps } from "./List.types";

const ERROR_MESSAGE_INVALID_CHILD =
	"Error in Design System List: Invalid child component.";

const List = React.forwardRef<HTMLElement, ListProps>(
	(
		{
			colorOn,
			gap,
			columnGap = "contracted",
			rowGap = "contracted",
			children,
			className,
			eventNotifications,
			onMouseDown,
			onSelectItem,
			...props
		},
		ref
	) => {
		const block = new BemIt("List");
		const [focusIndex, setFocusIndex] = useState(-1);
		const listRef = useRef<HTMLElement>(null);
		const focussedItemRef = useRef<HTMLLIElement>(null);
		let childCount = 0;

		useImperativeHandle(ref, () => listRef?.current as HTMLElement);

		// Resolve gap
		if (gap) {
			rowGap = columnGap = gap;
		}

		const renderChildren = Children.map(children, (child, index) => {
			if (isValidElement(child)) {
				const valid = child.type === ListItem;
				if (!valid) {
					console.error(ERROR_MESSAGE_INVALID_CHILD, child.type);
					return null;
				}
				childCount++;
				return cloneElement(child as React.ReactElement, {
					...(index === focusIndex && {
						focussed: true,
						ref: focussedItemRef,
					}),
					colorOn,
				});
			}
		});

		const selectItem = useCallback(
			(selectedValue?: string) => {
				if (onSelectItem) {
					onSelectItem(selectedValue);
				}
				setFocusIndex(-1);
			},
			[onSelectItem]
		);

		const handleListMouseDown = (event: React.MouseEvent<HTMLUListElement>) => {
			const VALUE = (event.target as HTMLElement).closest("li")?.value;
			if (VALUE) {
				selectItem(VALUE.toString());
			}
			if (onMouseDown) {
				onMouseDown(event);
			}
		};

		useEffect(() => {
			const isFocussedItemVisible = (): boolean => {
				if (focussedItemRef.current) {
					const listBounds = listRef.current?.getBoundingClientRect();
					const item = focussedItemRef.current;
					const itemBounds = item?.getBoundingClientRect();

					if (itemBounds.top < (listBounds?.top || 0)) {
						return false;
					} else if (
						itemBounds.top >
						(listRef.current?.offsetHeight || 0) + (listBounds?.top || 0)
					) {
						return false;
					}
				}
				return true;
			};

			if (eventNotifications) {
				switch (true) {
					case eventNotifications.selected: {
						selectItem(focussedItemRef.current?.value.toString());
						break;
					}
					case eventNotifications.moveBack: {
						if (focussedItemRef.current && !isFocussedItemVisible()) {
							focussedItemRef.current.scrollIntoView();
						}
						setFocusIndex((oldIndex) => {
							if (oldIndex > 0) {
								return oldIndex - 1;
							} else {
								const list = listRef.current;
								list?.scrollTo({ top: list.scrollHeight });
								return childCount - 1;
							}
						});
						break;
					}
					case eventNotifications.moveNext: {
						if (focussedItemRef.current && !isFocussedItemVisible()) {
							focussedItemRef.current.scrollIntoView();
						}
						setFocusIndex((oldIndex) => {
							if (oldIndex < childCount - 1) {
								return oldIndex + 1;
							} else {
								listRef.current?.scrollTo({ top: 0 });
								return 0;
							}
						});
						break;
					}
					default: {
						break;
					}
				}
			}
		}, [eventNotifications, childCount, selectItem]);

		useEffect(() => {
			const resolveAutoScrolling = () => {
				const listBounds = listRef.current?.getBoundingClientRect();
				const item = focussedItemRef.current;
				const itemBounds = item?.getBoundingClientRect();

				const listBottomEdge =
					(listBounds?.top || 0) + (listRef.current?.offsetHeight || 0);
				const itemBottomEdge =
					(itemBounds?.top || 0) + (item?.offsetHeight || 0);

				if (itemBottomEdge > listBottomEdge) {
					listRef.current?.scrollBy({
						top: item?.offsetHeight,
					});
				} else if ((itemBounds?.top || 0) < (listBounds?.top || 0)) {
					listRef.current?.scrollBy({
						top: -(item?.offsetHeight || 0),
					});
				}
			};

			if (focussedItemRef.current) {
				resolveAutoScrolling();
			}
		});

		return (
			<StyledList
				htmlTag="ul"
				ref={listRef}
				className={addClass(className).after(block)}
				columnGap={columnGap}
				rowGap={rowGap}
				onMouseDown={handleListMouseDown}
				{...props}
			>
				{renderChildren}
			</StyledList>
		);
	}
);

export default List;
